<template>
  <section id="admin-wallets">
    <b-card>
      <div class="page-top">
        <div>
          <h4 class="mb-0">کیف پول مشتریان</h4>
          <p class="text-muted mb-0">هر تغییرِ دستی با دلیل و نام ادمین در دفتر ثبت می‌شود و پاک‌شدنی نیست.</p>
        </div>
      </div>

      <div class="kpis">
        <div class="kpi">
          <div class="text-muted small">مجموع موجودی کیف پول‌ها</div>
          <div class="v">{{ toman(stats.totalBalance) }}</div>
          <div class="text-muted small">تومان — بدهیِ ما به {{ fa(stats.walletsWithBalance) }} مشتری</div>
        </div>
        <div class="kpi">
          <div class="text-muted small">شارژ امروز</div>
          <div class="v">{{ toman(stats.topupToday.amount) }}</div>
          <div class="text-muted small">{{ fa(stats.topupToday.count) }} تراکنش</div>
        </div>
        <div class="kpi">
          <div class="text-muted small">خرید با کیف پول (۳۰ روز)</div>
          <div class="v">{{ toman(stats.purchases30d.amount) }}</div>
          <div class="text-muted small">{{ fa(stats.purchases30d.count) }} پرداخت</div>
        </div>
        <div class="kpi" :class="{ warn: stats.unsettled > 0 }">
          <div class="text-muted small">پرداخت‌های بلاتکلیف</div>
          <div class="v">{{ fa(stats.unsettled) }}</div>
          <router-link :to="{ name: 'admin-payment-transactions' }" class="small">بررسی در تراکنش‌ها</router-link>
        </div>
      </div>

      <div class="grid">
        <div class="panel">
          <div class="panel-head">
            <h5 class="mb-0">مشتریان</h5>
            <div class="search-box">
              <v-icon size="18" color="#94a3b8">mdi-magnify</v-icon>
              <input v-model="search" placeholder="موبایل یا نام" @input="onSearch">
            </div>
          </div>
          <div v-if="listLoading" class="text-center py-4"><b-spinner small variant="primary" /></div>
          <table v-else class="w-100">
            <thead>
              <tr><th>مشتری</th><th>موبایل</th><th>موجودی (تومان)</th><th>آخرین گردش</th></tr>
            </thead>
            <tbody>
              <tr v-if="!rows.length">
                <td colspan="4" class="text-center text-muted py-3">{{ search ? 'کاربری پیدا نشد' : 'هنوز کیف پولی ساخته نشده؛ برای اصلاحِ موجودی، مشتری را جست‌وجو کنید' }}</td>
              </tr>
              <tr
                v-for="row in rows"
                :key="row.user && row.user._id"
                :class="{ active: selected && row.user && selected._id === row.user._id }"
                @click="openLedger(row.user)"
              >
                <td>
                  {{ name(row.user) }}
                  <b-badge v-if="row.user && row.user.type === 1" variant="light-info" pill>نماینده</b-badge>
                </td>
                <td><span class="ltr">{{ row.user && row.user.mobile }}</span></td>
                <td class="font-weight-bold">{{ toman(row.balance) }}</td>
                <td>{{ row.updatedAt ? date(row.updatedAt) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="panel">
          <template v-if="selected">
            <div class="panel-head">
              <h5 class="mb-0">{{ name(selected) }}</h5>
              <span class="mr-auto">موجودی: <b>{{ toman(ledger.balance) }} تومان</b></span>
            </div>

            <div class="adjust">
              <div class="seg">
                <button type="button" :class="{ on: direction === 1 }" @click="direction = 1">افزایش</button>
                <button type="button" :class="{ on: direction === -1 }" @click="direction = -1">کاهش</button>
              </div>
              <div class="field">
                <input :value="amountText" inputmode="numeric" placeholder="مبلغ" @input="onAmount($event.target.value)">
                <span class="text-muted">تومان</span>
              </div>
              <div class="field">
                <input v-model="reason" maxlength="300" placeholder="دلیل (اجباری) — مثلاً بازگشت وجهِ پرداختِ دوباره">
              </div>
              <div v-if="amount" class="text-muted small">
                موجودی بعد از ثبت: {{ toman(ledger.balance + direction * amount * 10) }} تومان
              </div>
              <b-button variant="primary" block :disabled="!canAdjust || saving" @click="confirmAdjust">
                <b-spinner v-if="saving" small class="ml-1" />
                ثبت در دفتر
              </b-button>
            </div>

            <h6 class="mt-3">دفتر حساب</h6>
            <div v-if="ledgerLoading" class="text-center py-3"><b-spinner small /></div>
            <div v-else-if="!ledger.rows.length" class="text-muted small">بدون گردش</div>
            <div v-for="tx in ledger.rows" :key="tx._id" class="ledger-row">
              <div>
                <div>{{ tx.description || typeText(tx.type) }}</div>
                <div class="text-muted small">
                  {{ date(tx.createdAt) }} — {{ typeText(tx.type) }}
                  <span v-if="tx.ref"> — <span class="ltr">{{ tx.ref }}</span></span>
                  <span v-if="tx.adminName"> — ثبت: {{ tx.adminName }}</span>
                </div>
              </div>
              <div class="text-left">
                <div :class="tx.amount > 0 ? 'text-success' : 'text-danger'" class="font-weight-bold">
                  {{ tx.amount > 0 ? '+' : '−' }}{{ toman(Math.abs(tx.amount)) }}
                </div>
                <div class="text-muted small">مانده {{ toman(tx.balanceAfter) }}</div>
              </div>
            </div>
          </template>
          <div v-else class="text-center text-muted py-5">
            یک مشتری را انتخاب کنید
          </div>
        </div>
      </div>
    </b-card>
  </section>
</template>

<script>
import {
  BCard, BButton, BBadge, BSpinner,
} from 'bootstrap-vue'
import PaymentService from '@/services/payment.service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard, BButton, BBadge, BSpinner,
  },
  data() {
    return {
      stats: {
        totalBalance: 0, walletsWithBalance: 0, topupToday: { amount: 0, count: 0 }, purchases30d: { amount: 0, count: 0 }, unsettled: 0,
      },
      rows: [],
      search: '',
      listLoading: false,
      selected: null,
      ledger: { balance: 0, rows: [] },
      ledgerLoading: false,
      direction: 1,
      amount: 0,
      reason: '',
      saving: false,
    }
  },
  computed: {
    amountText() {
      return this.amount ? this.fa(String(this.amount).replace(/\B(?=(\d{3})+(?!\d))/g, '٬')) : ''
    },
    canAdjust() {
      return this.amount > 0 && this.reason.trim().length >= 5 && (this.direction > 0 || this.amount * 10 <= this.ledger.balance)
    },
  },
  mounted() {
    this.loadStats()
    this.loadList()
  },
  methods: {
    async loadStats() {
      const res = await PaymentService.getWalletStats().catch(() => null)
      if (res && res.status === 'success') this.stats = res.result
    },
    async loadList() {
      this.listLoading = true
      try {
        const res = await PaymentService.getWalletList({ search: this.search })
        this.rows = res.status === 'success' ? res.result : []
      } finally {
        this.listLoading = false
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.loadList, 400)
    },
    async openLedger(user) {
      if (!user) return
      this.selected = user
      this.ledgerLoading = true
      try {
        const res = await PaymentService.getWalletLedger({ customerId: user._id, limit: 50 })
        if (res.status === 'success') this.ledger = res.result
      } finally {
        this.ledgerLoading = false
      }
    },
    onAmount(text) {
      const digits = String(text).replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[^\d]/g, '')
      this.amount = digits ? Math.min(Number(digits), 100000000) : 0
      this.$forceUpdate()
    },
    async confirmAdjust() {
      const verb = this.direction > 0 ? 'افزایش' : 'کاهش'
      const ok = await this.$bvModal.msgBoxConfirm(
        `${verb} ${this.amountText} تومان برای ${this.name(this.selected)}؟ این ردیف از دفتر پاک نمی‌شود.`,
        {
          title: 'تأیید اصلاح موجودی', okTitle: 'ثبت', cancelTitle: 'انصراف', centered: true,
        },
      )
      if (!ok) return
      this.saving = true
      try {
        const res = await PaymentService.adjustWallet({ customerId: this.selected._id, amount: this.direction * this.amount, reason: this.reason.trim() })
        if (res.status !== 'success') throw new Error(res.message)
        this.toast('ثبت شد', `موجودی جدید: ${this.toman(res.result.balance)} تومان`, 'success')
        this.amount = 0
        this.reason = ''
        await Promise.all([this.openLedger(this.selected), this.loadList(), this.loadStats()])
      } catch (error) {
        this.toast('ثبت نشد', error.message, 'danger')
      } finally {
        this.saving = false
      }
    },
    name(user) {
      if (!user) return '—'
      return `${user.name || ''} ${user.family || ''}`.trim() || user.mobile
    },
    typeText(type) {
      return {
        topup: 'شارژ از درگاه', purchase: 'خرید/تمدید', refund: 'بازگشت وجه', adjust: 'اصلاح دستی',
      }[type] || type
    },
    date(value) {
      return new Date(value).toLocaleString('fa-IR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
    toman(rial) {
      return new Intl.NumberFormat('fa-IR').format(Math.round((rial || 0) / 10))
    },
    fa(value) {
      return String(value === undefined ? '' : value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
    },
    toast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, icon: variant === 'danger' ? 'XIcon' : 'CheckIcon', variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.page-top {
  margin-bottom: 16px;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.kpi {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
}

.kpi.warn {
  border-color: #fbbf24;
  background: #fffbeb;
}

.kpi .v {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 16px;
  align-items: start;
}

.panel {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
}

.panel-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.search-box {
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 4px 10px;
  width: 240px;
}

.search-box input {
  border: 0;
  outline: 0;
  flex: 1;
  min-width: 0;
  font-family: inherit;
}

th {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  padding: 8px;
  border-bottom: 1px solid #e2e8f0;
}

td {
  padding: 10px 8px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

tbody tr {
  cursor: pointer;
}

tbody tr:hover,
tbody tr.active {
  background: #f0f9ff;
}

.ltr {
  display: inline-block;
  direction: ltr;
  unicode-bidi: isolate;
}

.adjust {
  display: grid;
  gap: 8px;
}

.seg {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.seg button {
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 7px;
  background: #fff;
  font-family: inherit;
  font-weight: 700;
}

.seg button.on {
  border-color: #0ea5e9;
  background: #f0f9ff;
  color: #0284c7;
}

.field {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 7px 12px;
}

.field input {
  border: 0;
  outline: 0;
  flex: 1;
  min-width: 0;
  font-family: inherit;
}

.ledger-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

@media (max-width: 991px) {
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .grid {
    grid-template-columns: 1fr;
  }

  .search-box {
    width: auto;
  }
}
</style>
